import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { DEFAULT_TAG_PALETTE, TAG_PALETTES } from "@lifeat/shared/task-management/TaskTagConstants";
import { validateUpdateTagNameRequest } from "@lifeat/shared/task-management/TaskTagUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { PopUpMenuComponents } from "@common/LifeAtPopupMenuV2/PopUpMenuComponents";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import PlusIcon from "@icons/Plus-Icon.svg";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import Styles from "@themes/Styles";
import { FilterTasksByTagEvent } from "../AmplitudeTaskFilterEvents";
import { useTaskListContext } from "../TaskListContext";
import TagCreationMenu from "./TagCreationMenu";
import { DefaultTagIndicator, EditButtonPopupTrigger, EditTagButton } from "./TaskTagDropdownComponents";

const TAGS_HEADER = "Tags";
const ALL_OPTION_LABEL = "All";
const CREATE_TAG_LABEL = "new tag";

export const FilteringContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    gap: 5px;
    padding: 0 10px;
    overflow: auto;
`;

export const SectionHeader = styled.span`
    margin-bottom: 5px;
    font-size: ${FontSize.normal};
    font-weight: 550;
`;

export const FilterItemsList = styled.div`
    overflow-y: auto;

    &:hover {
        ${ScrollbarStyles};
    }
`;

export const FilterItem = styled.div<{ color?: string }>`
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: ${Styles.borderRadiusSharp};
    font-size: ${FontSize.normal};
    cursor: default;

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
    }

    &:hover ${EditButtonPopupTrigger} {
        visibility: visible;
    }
`;

export const FilterCheckbox = styled(LifeAtCheckbox)<{ color?: string }>`
    .styled-checkbox {
        border-radius: ${Styles.borderRadiusSharp};
        ${(props) =>
            props.color &&
            css`
                border-color: ${props.color};
            `};
    }
    .checkbox-checkmark {
        scale: 0.8;
        ${(props) =>
            props.color &&
            css`
                stroke: ${props.color} !important;
            `};
    }
`;

const StyledPopupMenuTrigger = styled(PopUpMenuComponents.Trigger)`
    width: 100%;
`;

export const EmojiSpan = styled.span`
    margin-right: 4px;
`;
export const FilterItemTitle = styled.span<{ textColor: string }>`
    color: ${(props) => props.textColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const StyledPlusIcon = styled(PlusIcon)`
    margin-right: 4px;
    stroke: ${(props) => props.theme.iconColor};
`;

export function TaskTagFilterSidebarList() {
    const amplitudeService = AmplitudeService.getInstance();
    const { lightMode } = useAppearanceContext();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const { filters, tags, toggleTagFilter, clearTagFilters, defaultTagId } = useTaskListContext();
    const selectedTagIdSet = useMemo(() => {
        const ret = new Set<string>();
        for (const filter of filters) {
            if (filter.type === "tag") {
                ret.add(filter.tag);
            }
        }
        return ret;
    }, [filters]);
    const validateTagName = (tagName: string) => {
        return validateUpdateTagNameRequest(
            tagName,
            Object.values(tags).map((item) => item.document),
        );
    };

    return (
        <FilteringContainer>
            <SectionHeader>{TAGS_HEADER}</SectionHeader>
            <FilterItemsList>
                <FilterItem
                    onClick={() => {
                        clearTagFilters();
                    }}
                >
                    <FilterCheckbox checked={selectedTagIdSet.size === 0} />
                    {ALL_OPTION_LABEL}
                </FilterItem>
                {tags.map(({ id: tagId, document: tag }) => {
                    const palette = TAG_PALETTES.find((c) => c.primary === tag.color) ?? DEFAULT_TAG_PALETTE;
                    const textColor = lightMode ? palette.darker : palette.lighter;
                    return (
                        <FilterItem
                            key={tagId}
                            color={tag.color ?? undefined}
                            onClick={() => {
                                toggleTagFilter(tagId);
                                amplitudeService.logEvent(
                                    new FilterTasksByTagEvent({
                                        source: "board-view",
                                        selectedTagCount: selectedTagIdSet.size,
                                    }),
                                );
                            }}
                        >
                            <FilterCheckbox checked={selectedTagIdSet.has(tagId)} color={tag.color ?? undefined} />
                            {tag.emoji && <EmojiSpan>{tag.emoji}</EmojiSpan>}
                            {tag.name && <FilterItemTitle textColor={textColor}>{tag.name}</FilterItemTitle>}
                            {tagId === defaultTagId && <DefaultTagIndicator />}
                            <EditTagButton tagId={tagId} tag={tag} />
                        </FilterItem>
                    );
                })}
            </FilterItemsList>
            <PopUpMenuComponents.Root open={isPopoverOpen} onOpenChange={(open: boolean) => setIsPopoverOpen(open)}>
                <StyledPopupMenuTrigger>
                    <FilterItem>
                        <StyledPlusIcon />
                        <span>{CREATE_TAG_LABEL}</span>
                    </FilterItem>
                </StyledPopupMenuTrigger>
                <PopUpMenuComponents.Content>
                    <TagCreationMenu
                        validateTagName={validateTagName}
                        handleClosePopup={() => setIsPopoverOpen(false)}
                    />
                </PopUpMenuComponents.Content>
            </PopUpMenuComponents.Root>
        </FilteringContainer>
    );
}
